import { useMemo, useCallback } from 'react';
import { useApp } from '@/context/AppContext';
import { projectDeletionSet, applyProjectUpdate, createProject as createCoreProject } from '@trackmind/core';
import type { Project } from '@trackmind/core';

export interface CreateProjectInput {
  name: string;
  description?: string;
  priority?: number;
  parent_id?: string;
  color?: string;
}

export function useProjects() {
  const { projects, tasks, saveProject, deleteProject: removeProject, deleteTask } = useApp();

  const createProject = useCallback(async (data: CreateProjectInput): Promise<Project> => {
    // Core's factory — the one the assistant uses. Building it here gave a
    // project made by name (the picker's "Create …", the board's "+ Project")
    // priority 5, while the form and the assistant default to 3.
    const newProject = createCoreProject(data);
    await saveProject(newProject);
    return newProject;
  }, [saveProject]);

  const updateProject = useCallback(async (id: string, data: Partial<Project>): Promise<Project> => {
    const existing = projects.find(p => p.id === id);
    if (!existing) {
      throw new Error(`Project ${id} not found`);
    }
    // applyProjectUpdate in core, not a spread here: it is the one place a
    // stored Project is merged with a change, so the assistant's edits and
    // the app's take the same path — including keeping completed_at
    // consistent with status.
    const updated = applyProjectUpdate(existing, data, new Date().toISOString());
    await saveProject(updated);
    return updated;
  }, [projects, saveProject]);

  // Helper: Check if a parent project is paused (recursive check up the tree)
  const isParentPaused = useCallback((projectId: string): boolean => {
    const project = projects.find(p => p.id === projectId);
    if (!project || !project.parent_id) return false;

    const parent = projects.find(p => p.id === project.parent_id);
    if (!parent) return false;

    if (parent.status === 'paused') return true;
    return isParentPaused(parent.id);
  }, [projects]);

  // Helper: Check if a project is effectively paused (directly or through parent)
  const isEffectivelyPaused = useCallback((projectId: string): boolean => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return false;
    if (project.status === 'paused') return true;
    return isParentPaused(projectId);
  }, [projects, isParentPaused]);

  // Helper: Get all descendant project IDs
  const getDescendantIds = useCallback((projectId: string): string[] => {
    const descendants: string[] = [];
    const addChildren = (parentId: string) => {
      for (const p of projects) {
        if (p.parent_id === parentId) {
          descendants.push(p.id);
          addChildren(p.id);
        }
      }
    };
    addChildren(projectId);
    return descendants;
  }, [projects]);

  // Helper: Get direct children of a project
  const getChildProjects = useCallback((projectId: string): Project[] => {
    return projects.filter(p => p.parent_id === projectId);
  }, [projects]);

  // Unpause all sub-projects of a given project
  const unpauseAllSubprojects = useCallback(async (parentProjectId: string): Promise<void> => {
    const descendantIds = getDescendantIds(parentProjectId);
    const pausedDescendants = projects.filter(
      p => descendantIds.includes(p.id) && p.status === 'paused'
    );

    // Unpause all paused descendants
    for (const project of pausedDescendants) {
      await saveProject({
        ...project,
        status: 'active',
        updated_at: new Date().toISOString(),
      });
    }
  }, [projects, getDescendantIds, saveProject]);

  // Delete project with cascade delete of sub-projects
  /**
   * Delete a project, its whole subtree, AND every task inside them.
   *
   * The tasks are the fix. This used to remove project rows only, while the
   * confirmation dialog said in as many words "N tasks will be deleted" —
   * so every one of them survived carrying a `project_id` that no longer
   * resolved. Unreachable in the UI (task lists are entered through a
   * project) but still counted by analytics and still carried in every sync
   * payload from then on.
   *
   * Order is children-before-parents so a device replaying these tombstones
   * never briefly holds an orphan.
   */
  const deleteProjectWithCascade = useCallback(async (projectId: string): Promise<void> => {
    const { projectIds, taskIds } = projectDeletionSet(projects, tasks, projectId);
    for (const id of taskIds) await deleteTask(id);
    for (const id of projectIds) await removeProject(id);
  }, [projects, tasks, deleteTask, removeProject]);

  /** What a cascade would remove — so the dialog can state the real numbers. */
  const previewProjectDeletion = useCallback(
    (projectId: string) => projectDeletionSet(projects, tasks, projectId),
    [projects, tasks],
  );

  // Filter helpers
  const activeProjects = useMemo(() => projects.filter(p => p.status === 'active'), [projects]);
  const completedProjects = useMemo(() => projects.filter(p => p.status === 'completed'), [projects]);
  const pausedProjects = useMemo(() => projects.filter(p => p.status === 'paused'), [projects]);

  // Top-level projects (no parent)
  const topLevelProjects = useMemo(() => projects.filter(p => !p.parent_id), [projects]);

  return {
    projects,
    activeProjects,
    completedProjects,
    pausedProjects,
    topLevelProjects,
    loading: false,
    error: null,
    refetch: () => Promise.resolve(),
    createProject,
    updateProject,
    deleteProject: deleteProjectWithCascade,  // Use cascade delete by default
    deleteProjectSingle: removeProject,  // For deleting a single project without cascade
    previewProjectDeletion,
    // Pause inheritance helpers
    isParentPaused,
    isEffectivelyPaused,
    getDescendantIds,
    getChildProjects,
    unpauseAllSubprojects,
  };
}
